import React, { useEffect, useMemo, useState } from 'react';
import {
  assignToggleStyle,
  boardPanelStyle,
  chipStyle,
  chipWrapStyle,
  dashboardGridStyle,
  emptyNoticeStyle,
  itemGroupCountStyle,
  itemGroupHeaderStyle,
  listScrollStyle,
  panelBodyStyle,
  panelHeaderStyle,
  panelSubStyle,
  panelTitleStyle,
  rowMetaStyle,
  rowTitleStyle,
  searchStyle,
  selectableRowStyle,
} from './dashboardStyles';
import { compareGroupedEmployee, getEmployeeCompanyGroup, groupEmployees } from './employeeGrouping';
import { getItemCompanyGroup, getMenuOrder, groupItems } from './itemGrouping';
import { useTenantMenuInfo } from '../adminlayout/tenantMenuInfo';

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function itemMatches(item, keyword) {
  const q = normalize(keyword);
  if (!q) return true;
  return [item.label, item.code, item.description, item.companyGroup?.label]
    .some((value) => normalize(value).includes(q));
}

function employeeMatches(emp, keyword) {
  const q = normalize(keyword);
  if (!q) return true;
  return [emp.name, emp.loginId, emp.department, emp.employeeNo, emp.companyGroup?.label, emp.companyGroup?.code]
    .some((value) => normalize(value).includes(q));
}

function kindLabel(kind) {
  return kind === 'feature' ? '기능' : '업무';
}

export default function PermissionItemView({
  overview,
  isMobile,
  savingUserId,
  onGrant,
  onRevoke,
}) {
  const [itemKeyword, setItemKeyword] = useState('');
  const [employeeKeyword, setEmployeeKeyword] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [selectedKey, setSelectedKey] = useState(null);
  // 일반 메뉴 묶음 머리글 = 보는 사람 테넌트의 대표 회사 이름(모르면 'PINE' — 종전)
  const tenantInfo = useTenantMenuInfo();
  const hqLabel = tenantInfo.primaryName || null;
  // 직원 목록 머리글도 같은 이름
  const hqCompany = useMemo(
    () => ({ code: tenantInfo.primaryCode, name: tenantInfo.primaryName }),
    [tenantInfo.primaryCode, tenantInfo.primaryName],
  );

  const items = useMemo(() => {
    const all = overview?.itemSummaries || [];
    return all
      .map((item, index) => ({
        ...item,
        companyGroup: getItemCompanyGroup(item, hqLabel),
        menuOrder: getMenuOrder(item, index),
        fallbackOrder: Number.isFinite(Number(item.sortIndex)) ? Number(item.sortIndex) : index,
      }))
      .sort((a, b) => (
        a.companyGroup.order - b.companyGroup.order
        || String(a.companyGroup.label || '').localeCompare(String(b.companyGroup.label || ''), 'ko')
        || a.menuOrder - b.menuOrder
        || a.fallbackOrder - b.fallbackOrder
        || String(a.label || '').localeCompare(String(b.label || ''), 'ko')
      ))
      .filter((item) => kindFilter === 'all' || item.kind === kindFilter)
      .filter((item) => itemMatches(item, itemKeyword));
  }, [overview, itemKeyword, kindFilter, hqLabel]);

  const itemGroups = useMemo(() => {
    return groupItems(items);
  }, [items]);

  useEffect(() => {
    if (!selectedKey && items[0]) setSelectedKey(items[0].key);
    if (selectedKey && items.length && !items.some((item) => item.key === selectedKey)) {
      setSelectedKey(items[0].key);
    }
  }, [items, selectedKey]);

  const selected = items.find((item) => item.key === selectedKey) || items[0] || null;
  const holderIds = new Set((selected?.holders || []).map((row) => Number(row.userId)));
  const employees = useMemo(() => (
    (overview?.employeeSummaries || [])
      .map((emp) => ({ ...emp, companyGroup: getEmployeeCompanyGroup(emp, hqCompany) }))
      .sort(compareGroupedEmployee)
      .filter((emp) => employeeMatches(emp, employeeKeyword))
  ), [overview, employeeKeyword, hqCompany]);
  const employeeGroups = useMemo(() => groupEmployees(employees), [employees]);

  function makeSpec(userId) {
    if (!selected) return null;
    if (selected.kind === 'feature') {
      return { kind: 'feature', featureSetId: selected.featureSetId, userId };
    }
    return { kind: 'task', taskCode: selected.code, userId };
  }

  if (!overview) return <div style={emptyNoticeStyle}>현황을 불러오는 중입니다.</div>;

  return (
    <div style={dashboardGridStyle(isMobile)}>
      <section style={boardPanelStyle}>
        <div style={panelHeaderStyle}>
          <h3 style={panelTitleStyle}>업무·기능</h3>
          <span style={panelSubStyle}>{items.length}개</span>
        </div>
        <div style={panelBodyStyle}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'all', label: '전체' },
              { key: 'task', label: '업무' },
              { key: 'feature', label: '기능' },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                style={assignToggleStyle(kindFilter === item.key)}
                onClick={() => setKindFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <input
            style={searchStyle}
            placeholder="업무/기능명 검색"
            value={itemKeyword}
            onChange={(e) => setItemKeyword(e.target.value)}
          />
          <div style={listScrollStyle}>
            {items.length === 0 && <div style={emptyNoticeStyle}>검색 결과가 없습니다.</div>}
            {itemGroups.map((group) => (
              <div key={group.key}>
                <div style={itemGroupHeaderStyle}>
                  <span>{group.label}</span>
                  <span style={itemGroupCountStyle}>{group.items.length}</span>
                </div>
                {group.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    style={selectableRowStyle(item.key === selected?.key)}
                    onClick={() => setSelectedKey(item.key)}
                  >
                    <div style={rowTitleStyle}>{item.label}</div>
                    <div style={rowMetaStyle}>{kindLabel(item.kind)} · {item.code || item.description || '-'}</div>
                    <div style={chipWrapStyle}>
                      <span style={chipStyle}>배정 {item.holderCount}</span>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={boardPanelStyle}>
        <div style={panelHeaderStyle}>
          <h3 style={panelTitleStyle}>{selected ? selected.label : '배정 직원'}</h3>
          {selected && <span style={panelSubStyle}>{selected.holderCount}명 배정</span>}
        </div>
        <div style={panelBodyStyle}>
          {!selected ? (
            <div style={emptyNoticeStyle}>업무 또는 기능을 선택해 주세요.</div>
          ) : (
            <>
              <input
                style={searchStyle}
                placeholder="직원명, 아이디, 부서 검색"
                value={employeeKeyword}
                onChange={(e) => setEmployeeKeyword(e.target.value)}
              />
              <div style={listScrollStyle}>
                {employeeGroups.map((group) => (
                  <div key={group.key}>
                    <div style={itemGroupHeaderStyle}>
                      <span>{group.label}</span>
                      <span style={itemGroupCountStyle}>{group.items.length}</span>
                    </div>
                    {group.items.map((emp) => {
                      const assigned = holderIds.has(Number(emp.id));
                      const spec = makeSpec(emp.id);
                      return (
                        <div key={emp.id} style={selectableRowStyle(false)}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', gap: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={rowTitleStyle}>{emp.name}</div>
                              <div style={rowMetaStyle}>
                                {emp.employeeNo ? `사번 ${emp.employeeNo} · ` : ''}{emp.loginId} · {emp.department || '-'}
                              </div>
                            </div>
                            <button
                              type="button"
                              style={assignToggleStyle(assigned)}
                              disabled={savingUserId === emp.id}
                              onClick={() => (assigned ? onRevoke(spec) : onGrant(spec))}
                            >
                              {assigned ? '배정됨' : '배정'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {employees.length === 0 && <div style={emptyNoticeStyle}>검색 결과가 없습니다.</div>}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
